import { FALLBACK_INWORLD_VOICES, INWORLD_VOICE_ID_PATTERN, LANGUAGE_INFO } from '../../Mobx/AudioCatalog';

/**
 * The logic of the "bulk run" tab of the audio page (SCRUM-1847): reading what an admin typed, building the request for the
 * server, and the words for what the server answers. No React and no network here, so it is tested on its own.
 *
 * The same request is sent to plan a run (a dry run that creates nothing) and to start it - the start adds the number of files
 * that the plan showed: expectedMissingFiles (mode fill-missing), expectedReplaceFiles (mode replace-with-backup) or expectedTrialFiles (mode trial).
 * The rules of the request are the ones of 4_services/ai/text_to_speech/bulk/config.js on the server, which checks everything again.
 *
 * Three modes: fill-missing creates the files that are missing (a file that exists is never touched); replace-with-backup replaces the files
 * that exist, each one only after its old version was copied to a backup folder of the run (the server never deletes anything); trial makes
 * every file in a folder of its own, where only the admin listens to it, and touches nothing on the site.
 *
 * Any mode can also run as a group run (SCRUM-1882): ONE request to the speech service for a group of words, cut into the files of the
 * words by the silences between them. It works with Google only, one request at a time and with no fallback; a trial is always a group run.
 * The server keeps the whole recording of every group, with the time of every word in it, and the admin listens to it from the run (SCRUM-1882).
 */

export const MODE_FILL_MISSING = 'fill-missing';
export const MODE_REPLACE_WITH_BACKUP = 'replace-with-backup';
export const MODE_TRIAL = 'trial';
export const MODES = [MODE_FILL_MISSING, MODE_REPLACE_WITH_BACKUP, MODE_TRIAL];
export const MODE_LABELS = { [MODE_FILL_MISSING]: 'יצירת קבצים חסרים', [MODE_REPLACE_WITH_BACKUP]: 'החלפה עם גיבוי', [MODE_TRIAL]: 'ניסוי (לא נוגע באתר)' };
/** Where the old files of a replacement run are kept: <prefix><run id>/ (the server sends the same in /options) */
export const BACKUP_PREFIX = 'files-backup/audio/';
/** Where the files of a trial run are made: <prefix><run id>/ - nobody hears them on the site (the server sends the same in /options) */
export const TRIAL_PREFIX = 'files-trials/audio/';
/** Where the whole recording of every group is kept, privately: <prefix><run id>/ (the server sends the same in /options) */
export const GROUPS_PREFIX = 'files-groups/audio/';
/** A path in a Hebrew sentence stays in one piece (it moves to the next line as a whole, and is broken only when the line is shorter than the path) */
export const PATH_STYLE = { display: 'inline-block', maxWidth: '100%', overflowWrap: 'anywhere' };
export const SOURCE_IDS = 'ids';
export const SOURCE_FILTER = 'filter';
export const GENDERS = ['female', 'male'];
export const GENDER_LABELS = { female: 'נקבה', male: 'זכר' };
export const LISTS = ['A', 'B', 'C', 'D', 'E'];
export const PROVIDER_LABELS = { google: 'Google (Gemini)', inworld: 'Inworld' };
/** For a narrow place (a cell of a table, next to a voice) */
export const PROVIDER_SHORT_LABELS = { google: 'Google', inworld: 'Inworld' };
export const OBJECT_ID_PATTERN = /^[0-9a-f]{24}$/i;

/** What the server allows - used until the options are loaded (the numbers of config.js on the server) */
export const FALLBACK_LIMITS = { maxEntries: 2000, maxFiles: 5000, maxReplaceFiles: 1000, maxTrialFiles: 800, maxCostUsd: 50, maxRpm: 120, maxConcurrency: 4, defaultEntryLimit: 500 };
export const FALLBACK_PACING = { google: { rpm: 8, concurrency: 1 }, inworld: { rpm: 120, concurrency: 3 } };
/** The group mode (GROUP_LIMITS of the server): the size of a group in words, and what one request holds */
export const FALLBACK_GROUP_LIMITS = { minSize: 2, maxSize: 400, defaultSize: 100, maxItemChars: 40, maxRequestChars: 3600, maxRequestSeconds: 520 };

/** The fields of an entry that a bulk run can speak, and the languages that come from each of them (the server sends the same) */
const FALLBACK_FIELD_LANGUAGES = {
    heb_to_eng: { original: ['he'], trns: ['en_US', 'en_GB'] },
    eng_to_heb: { original: ['en_US', 'en_GB'], trns: ['he'] },
};

/** The two voices that were chosen for Hebrew with Google; Gemini voices speak every language */
const SUGGESTED_GOOGLE_VOICES = { female: 'Callirrhoe', male: 'Enceladus' };

export const languageLabel = (language) => LANGUAGE_INFO[language]?.label ?? language;

/** "עברית: Callirrhoe (נקבה), Enceladus (זכר)" - who speaks, for every language of a plan or a run */
export const describeVoices = (voices) => Object.entries(voices ?? {})
    .map(([language, byGender]) => `${languageLabel(language)}: ${Object.entries(byGender ?? {}).map(([gender, voice]) => `${voice} (${GENDER_LABELS[gender] ?? gender})`).join(', ')}`)
    .join(' | ');

export const hebrewField = (dictionary) => (dictionary === 'heb_to_eng' ? 'original' : 'trns');

/** The mode of a plan or a run. A server that does not say (it does not have the mode of replacement yet) creates missing files. */
export const modeOf = (item) => item?.mode ?? item?.config?.mode ?? MODE_FILL_MISSING;
export const isReplaceMode = (mode) => mode === MODE_REPLACE_WITH_BACKUP;
export const isTrialMode = (mode) => mode === MODE_TRIAL;
/** Creating is always possible; the other modes only when the server says that it knows the mode (/options holds modes) */
export const supportsMode = (options, mode) => mode === MODE_FILL_MISSING || (Array.isArray(options?.modes) && options.modes.includes(mode));
/** The group mode only when the server says that it knows it (/options holds the limits of a group) */
export const supportsGroup = (options) => Boolean(options?.group) && typeof options.group === 'object';
export const backupPrefixOf = (options) => options?.backupPrefix ?? BACKUP_PREFIX;
export const trialPrefixOf = (options) => options?.trialPrefix ?? TRIAL_PREFIX;
export const groupsPrefixOf = (options) => options?.groupsPrefix ?? GROUPS_PREFIX;
/** "files-backup/audio/<run id>/" - where the old files of a replacement run will be, before the run has an id */
export const backupFolderPattern = (options) => `${backupPrefixOf(options)}<run id>/`;
/** "files-trials/audio/<run id>/" - where the files of a trial run will be, before the run has an id */
export const trialFolderPattern = (options) => `${trialPrefixOf(options)}<run id>/`;
/** "files-groups/audio/<run id>/" - where the whole recordings of the groups of a run will be kept (private), before the run has an id */
export const groupsFolderPattern = (options) => `${groupsPrefixOf(options)}<run id>/`;
/**
 * The files that a plan works on: the ones it would create, replace, or (a trial) make. The server says it in files.work - the number that
 * a start is compared with; a server that does not (it is older) is read by the mode.
 */
export const plannedFiles = (plan) => {
    if (Number.isFinite(plan?.files?.work)) return plan.files.work;
    return isReplaceMode(modeOf(plan)) ? (plan?.replace?.files ?? plan?.files?.existing ?? 0) : (plan?.files?.missing ?? 0);
};
/** The field that a start must hold: the number of files that the plan showed, under the name of its mode */
export const expectedFilesField = (request) => {
    if (isTrialMode(request?.mode)) return 'expectedTrialFiles';
    return isReplaceMode(request?.mode) ? 'expectedReplaceFiles' : 'expectedMissingFiles';
};

export const limitsOf = (options) => ({ ...FALLBACK_LIMITS, ...(options?.limits ?? {}) });
export const groupLimitsOf = (options) => ({ ...FALLBACK_GROUP_LIMITS, ...(options?.group ?? {}) });
export const pacingOf = (options, provider) => options?.pacing?.[provider] ?? FALLBACK_PACING[provider] ?? FALLBACK_PACING.google;

/** The size of the groups of a plan or a run in words (null for a run that is not a group run) */
export const groupSizeOf = (item) => {
    const size = item?.group?.size ?? item?.config?.group?.size;
    return Number.isFinite(size) ? size : null;
};
export const isGroupRun = (item) => groupSizeOf(item) !== null;

// ____________________________________________________________________________________________
// reading what was typed

/** "1,234" - a number the way it is read in the page */
export const formatNumber = (value) => Number(value ?? 0).toLocaleString('he-IL');

/** "מילה אחת", "5 מילים" (a number that is not there is none, so that the page never says "NaN מילים") */
export const wordsCount = (count) => {
    const number = Number(count);
    return number === 1 ? 'מילה אחת' : `${formatNumber(Number.isFinite(number) ? number : 0)} מילים`;
};

/** The estimate of the time of a run, from minutes: "פחות מדקה", "דקה אחת", "25 דקות", "כ-10.4 שעות (625 דקות)" */
export function formatDuration(minutes) {
    const value = Number(minutes);
    if (!Number.isFinite(value) || value <= 0) return 'פחות מדקה';
    const rounded = Math.round(value);
    if (rounded <= 1) return value < 0.5 ? 'פחות מדקה' : 'דקה אחת';
    if (rounded < 90) return `${formatNumber(rounded)} דקות`;
    return `כ-${(value / 60).toLocaleString('he-IL', { maximumFractionDigits: 1 })} שעות (${formatNumber(rounded)} דקות)`;
}

/** "$0.0204" for a tiny amount, "$1.50" otherwise */
export function formatUsd(value) {
    const number = Number(value ?? 0);
    if (!Number.isFinite(number)) return '$0.00';
    return `$${number > 0 && number < 0.01 ? number.toFixed(4) : number.toFixed(2)}`;
}

/** "3.11.2026, 20:41:05" in the time of the browser, or '' */
export function formatDateTime(value) {
    if (!value) return '';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '' : date.toLocaleString('he-IL');
}

/**
 * A place in a recording, from milliseconds: "0:05.3", "12:48.0", "1:02:03.4" - the minutes, the seconds and the tenth of a second,
 * which is the precision of the times that the server keeps. Always in this order (a clock is read from left to right, also in Hebrew).
 */
export function formatClock(ms) {
    const value = Number(ms);
    const tenths = Number.isFinite(value) && value > 0 ? Math.floor(value / 100) : 0;
    const totalSeconds = Math.floor(tenths / 10);
    const seconds = String(totalSeconds % 60).padStart(2, '0');
    const minutes = Math.floor(totalSeconds / 60) % 60;
    const hours = Math.floor(totalSeconds / 3600);
    const fraction = tenths % 10;
    return hours > 0
        ? `${hours}:${String(minutes).padStart(2, '0')}:${seconds}.${fraction}`
        : `${minutes}:${seconds}.${fraction}`;
}

/**
 * The ids that were pasted - separated by spaces, new lines, commas or semicolons. Quotes and brackets around an id (a list
 * copied from JSON) are ignored. Capital letters become small ones, and an id that appears twice counts once.
 * @returns {{ ids: string[], invalid: string[], duplicates: number }}
 */
export function parseIds(text) {
    const tokens = String(text ?? '').split(/[\s,;]+/)
        .map((token) => token.replace(/^[[\]"'`]+|[[\]"'`]+$/g, ''))
        .filter(Boolean);
    const seen = new Set();
    const ids = [];
    const invalid = [];
    let duplicates = 0;
    for (const token of tokens) {
        if (!OBJECT_ID_PATTERN.test(token)) {
            invalid.push(token);
            continue;
        }
        const id = token.toLowerCase();
        if (seen.has(id)) {
            duplicates += 1;
            continue;
        }
        seen.add(id);
        ids.push(id);
    }
    return { ids, invalid, duplicates };
}

/** Whole numbers separated by spaces, commas or semicolons: { values, invalid } */
export function parseIntegers(text) {
    const values = [];
    const invalid = [];
    for (const token of String(text ?? '').split(/[\s,;]+/).filter(Boolean)) {
        if (/^-?\d+$/.test(token)) {
            const number = Number(token);
            if (!values.includes(number)) values.push(number);
        } else {
            invalid.push(token);
        }
    }
    return { values, invalid };
}

/** '' -> undefined (not given), a number -> the number, anything else -> NaN */
const optionalNumber = (text) => {
    const trimmed = String(text ?? '').trim();
    if (trimmed === '') return undefined;
    return /^-?\d+(\.\d+)?$/.test(trimmed) ? Number(trimmed) : NaN;
};

// ____________________________________________________________________________________________
// the form

/** The languages that the fields of a dictionary are spoken in */
export function fieldLanguages(options, dictionary) {
    return options?.dictionaries?.find((item) => item.key === dictionary)?.fields ?? FALLBACK_FIELD_LANGUAGES[dictionary] ?? {};
}

export function impliedLanguages(options, dictionary, fields) {
    const map = fieldLanguages(options, dictionary);
    return [...new Set(fields.flatMap((field) => map[field] ?? []))];
}

export function initialForm(options, dictionary = options?.dictionaries?.[0]?.key ?? 'heb_to_eng') {
    const fields = [hebrewField(dictionary)];
    return {
        dictionary,
        mode: MODE_FILL_MISSING,
        source: SOURCE_IDS,
        idsText: '',
        bandsText: '',
        lists: [],
        priorityMin: '',
        priorityMax: '',
        createdFrom: '',
        createdTo: '',
        limit: '',
        afterId: '',
        fields,
        languages: impliedLanguages(options, dictionary, fields),
        genders: [...GENDERS],
        provider: 'google',
        // { google|inworld: { he|en_US|...: { female, male } } } - only what the admin chose; the rest comes from defaultVoice
        voices: {},
        useFallback: false,
        // the group mode: one request for a group of words (Google only, one request at a time, no fallback)
        useGroup: false,
        groupSize: String(groupLimitsOf(options).defaultSize),
        maxCostUsd: '',
        rpm: '',
        concurrency: '',
    };
}

/** What a group run needs from the rest of the form: Google, no fallback, one request at a time (the server refuses anything else) */
const groupedForm = (form) => ({ ...form, useGroup: true, provider: 'google', useFallback: false, concurrency: '' });

/** Another mode. A trial is always a group run, so it takes the settings of one. */
export function withMode(form, mode) {
    const next = { ...form, mode };
    return isTrialMode(mode) ? groupedForm(next) : next;
}

/** The group mode on or off. On: Google, no fallback and one request at a time. A trial cannot leave it. */
export function withGroup(form, enabled) {
    if (enabled) return groupedForm(form);
    return isTrialMode(form.mode) ? form : { ...form, useGroup: false };
}

/** Another dictionary: its Hebrew field is spoken, the other settings stay */
export function withDictionary(form, dictionary, options) {
    const fields = [hebrewField(dictionary)];
    return { ...form, dictionary, fields, languages: impliedLanguages(options, dictionary, fields) };
}

/** Other fields: a language that comes in with a new field is chosen, a language that the admin cleared stays cleared */
export function withFields(form, fields, options) {
    const before = impliedLanguages(options, form.dictionary, form.fields);
    const after = impliedLanguages(options, form.dictionary, fields);
    const languages = after.filter((language) => form.languages.includes(language) || !before.includes(language));
    return { ...form, fields, languages };
}

export function fallbackProviderOf(form) {
    return form.provider === 'google' ? 'inworld' : 'google';
}

/** The voice that is filled in before the admin chooses: the ones that were decided for the first runs, then the defaults of the server */
export function defaultVoice(options, provider, language, gender) {
    const suggested = options?.suggestedVoices?.[provider]?.[language]?.[gender];
    if (suggested) return suggested;
    if (provider === 'google') {
        const wanted = SUGGESTED_GOOGLE_VOICES[gender];
        const list = options?.voices?.google;
        return !list || list.includes(wanted) ? wanted : '';
    }
    return options?.voices?.inworld?.defaults?.[language]?.[gender]?.[0] ?? FALLBACK_INWORLD_VOICES[language]?.[gender]?.[0] ?? '';
}

export function voiceOf(form, options, provider, language, gender) {
    return form.voices?.[provider]?.[language]?.[gender] ?? defaultVoice(options, provider, language, gender);
}

export function withVoice(form, provider, language, gender, voice) {
    const voices = form.voices ?? {};
    const ofProvider = voices[provider] ?? {};
    const ofLanguage = ofProvider[language] ?? {};
    return { ...form, voices: { ...voices, [provider]: { ...ofProvider, [language]: { ...ofLanguage, [gender]: voice } } } };
}

export function isValidVoice(options, provider, voice) {
    if (typeof voice !== 'string' || voice === '') return false;
    if (provider === 'google') return options?.voices?.google ? options.voices.google.includes(voice) : true;
    let pattern = INWORLD_VOICE_ID_PATTERN;
    try {
        if (options?.voices?.inworld?.pattern) pattern = new RegExp(options.voices.inworld.pattern);
    } catch {
        // a pattern that cannot be read: the one of the admin is used
    }
    return pattern.test(voice);
}

/** What is said when the server (an older one) does not know a mode that was chosen */
const UNSUPPORTED_MODE_TEXT = {
    [MODE_REPLACE_WITH_BACKUP]: 'השרת עוד לא מכיר את מצב ההחלפה עם גיבוי. אחרי שהשרת יתעדכן המצב הזה יהיה זמין, ועד אז אפשר ליצור רק קבצים שחסרים.',
    [MODE_TRIAL]: 'השרת עוד לא מכיר את מצב הניסוי. אחרי שהשרת יתעדכן המצב הזה יהיה זמין, ועד אז אפשר ליצור רק קבצים שחסרים.',
};

/**
 * The request for the server, or the list of what is wrong in the form.
 * The order of the keys is fixed, so the same form always gives the same text (requestKey).
 * @returns {{ ok: boolean, errors: string[], request: object|null }}
 */
export function buildRequest(form, options) {
    const errors = [];
    const limits = limitsOf(options);
    const mode = MODES.includes(form.mode) ? form.mode : MODE_FILL_MISSING;
    // a trial is a try of the group mode, so it is always a group run
    const grouping = Boolean(form.useGroup) || isTrialMode(mode);
    const request = { dictionary: form.dictionary, mode };
    if (!supportsMode(options, mode)) {
        errors.push(UNSUPPORTED_MODE_TEXT[mode] ?? `השרת עוד לא מכיר את המצב "${MODE_LABELS[mode]}".`);
    }

    // the entries
    if (form.source === SOURCE_IDS) {
        const { ids, invalid } = parseIds(form.idsText);
        if (ids.length === 0 && invalid.length === 0) errors.push('צריך להדביק לפחות מזהה אחד של ערך.');
        if (invalid.length > 0) errors.push(`${formatNumber(invalid.length)} מזהים אינם תקינים (מזהה הוא 24 תווים: ספרות ואותיות a עד f), למשל: ${invalid[0].slice(0, 30)}`);
        if (ids.length > limits.maxEntries) errors.push(`אפשר עד ${formatNumber(limits.maxEntries)} ערכים בהרצה אחת, והודבקו ${formatNumber(ids.length)}.`);
        request.ids = ids;
    } else {
        const filter = {};
        const bands = parseIntegers(form.bandsText);
        if (bands.invalid.length > 0) errors.push(`בנדים הם מספרים שלמים בלבד (נמצא: ${bands.invalid[0].slice(0, 20)}).`);
        else if (bands.values.length > 50) errors.push('אפשר עד 50 בנדים.');
        else if (bands.values.length > 0) filter.bands = bands.values;
        if (form.lists.length > 0) filter.lists = LISTS.filter((list) => form.lists.includes(list));
        for (const key of ['priorityMin', 'priorityMax']) {
            const value = optionalNumber(form[key]);
            if (Number.isNaN(value)) errors.push(`${key === 'priorityMin' ? 'עדיפות מ-' : 'עדיפות עד'} חייבת להיות מספר.`);
            else if (value !== undefined) filter[key] = value;
        }
        for (const key of ['createdFrom', 'createdTo']) {
            if (form[key]) filter[key] = form[key];
        }
        request.filter = filter;
        const limit = optionalNumber(form.limit);
        if (limit !== undefined) {
            if (!Number.isInteger(limit) || limit < 1 || limit > limits.maxEntries) errors.push(`מספר הערכים לבדיקה הוא מספר שלם מ-1 עד ${formatNumber(limits.maxEntries)}.`);
            else request.limit = limit;
        }
        const afterId = String(form.afterId ?? '').trim();
        if (afterId !== '') {
            if (OBJECT_ID_PATTERN.test(afterId)) request.afterId = afterId.toLowerCase();
            else errors.push('המשך אחרי מזהה: המזהה חייב להיות 24 תווים (ספרות ואותיות a עד f).');
        }
    }

    // what is spoken
    const fields = form.fields.filter(Boolean);
    if (fields.length === 0) errors.push('צריך לבחור לפחות שדה אחד להקראה.');
    request.fields = fields;
    const languages = impliedLanguages(options, form.dictionary, fields).filter((language) => form.languages.includes(language));
    if (fields.length > 0 && languages.length === 0) errors.push('צריך לבחור לפחות שפה אחת.');
    request.languages = languages;
    const genders = GENDERS.filter((gender) => form.genders.includes(gender));
    if (genders.length === 0) errors.push('צריך לבחור לפחות מגדר אחד.');
    request.genders = genders;

    // who speaks
    const collectVoices = (provider, label) => {
        const voices = {};
        for (const language of languages) {
            voices[language] = {};
            for (const gender of genders) {
                const voice = voiceOf(form, options, provider, language, gender).trim();
                if (!isValidVoice(options, provider, voice)) {
                    errors.push(`${label}: חסר קול תקין (${GENDER_LABELS[gender]}, ${language}).`);
                } else {
                    voices[language][gender] = voice;
                }
            }
        }
        return voices;
    };
    request.provider = form.provider;
    request.voices = collectVoices(form.provider, PROVIDER_LABELS[form.provider]);
    if (form.useFallback) {
        const provider = fallbackProviderOf(form);
        request.fallback = { provider, voices: collectVoices(provider, `גיבוי ${PROVIDER_LABELS[provider]}`) };
    }

    // the group: ONE request for many words. An empty size is the size that the server suggests.
    if (grouping) {
        const groupLimits = groupLimitsOf(options);
        if (!supportsGroup(options)) errors.push('השרת עוד לא מכיר את מצב הקבוצה (בקשה אחת לקבוצה של מילים). אחרי שהשרת יתעדכן המצב הזה יהיה זמין.');
        const size = optionalNumber(form.groupSize);
        if (size === undefined) request.group = { size: groupLimits.defaultSize };
        else if (!Number.isInteger(size) || size < groupLimits.minSize || size > groupLimits.maxSize) errors.push(`גודל הקבוצה הוא מספר שלם של מילים, מ-${groupLimits.minSize} עד ${groupLimits.maxSize}.`);
        else request.group = { size };
        if (form.provider !== 'google') errors.push('מצב קבוצה עובד רק עם Google (Gemini).');
        if (form.useFallback) errors.push('במצב קבוצה אין ספק גיבוי: כשהמכסה נגמרת ההרצה מושהית, ואפשר להמשיך אותה.');
    }

    // the cost and the pace
    const maxCostUsd = optionalNumber(form.maxCostUsd);
    if (maxCostUsd !== undefined) {
        if (!(maxCostUsd > 0 && maxCostUsd <= limits.maxCostUsd)) errors.push(`תקרת העלות היא מספר מעל 0 ועד ${limits.maxCostUsd} דולר.`);
        else request.maxCostUsd = maxCostUsd;
    }
    for (const [key, label, max] of [['rpm', 'מספר הבקשות בדקה', limits.maxRpm], ['concurrency', 'מספר הבקשות במקביל', limits.maxConcurrency]]) {
        const value = optionalNumber(form[key]);
        if (value === undefined) continue;
        if (!Number.isInteger(value) || value < 1 || value > max) errors.push(`${label} הוא מספר שלם מ-1 עד ${max}.`);
        else if (key === 'concurrency' && grouping && value !== 1) errors.push('במצב קבוצה נשלחת בקשה אחת בכל פעם (בקשה לקבוצה נמשכת דקות, והמכסה נספרת בבקשות).');
        else request[key] = value;
    }

    return errors.length > 0 ? { ok: false, errors, request: null } : { ok: true, errors: [], request };
}

/** Two requests that are the same have the same key: the plan on the screen is the plan of the form only while the keys are equal */
export const requestKey = (request) => JSON.stringify(request);

// ____________________________________________________________________________________________
// what the server answers: the plan

const leadingNumber = (message) => Number.parseInt(String(message ?? '').match(/^\s*(\d+)/)?.[1] ?? '0', 10);

/**
 * A warning of the plan in Hebrew (the text of the server is English; an unknown kind of warning shows that text)
 * @param {object} warning
 * @param {string} [mode] the mode of the plan - a few words depend on it (a file is created, or replaced)
 */
export function describeWarning(warning, mode = MODE_FILL_MISSING) {
    const count = formatNumber(leadingNumber(warning?.message));
    switch (warning?.code) {
        case 'entries-not-found': return `${count} מהמזהים אינם ערכים במילון הזה, ולכן לא נכללו.`;
        case 'texts-skipped': return `${count} טקסטים ריקים או שיש בהם רק הערות וסימני פיסוק, ולכן אין מה להקריא והם לא נכללו.`;
        case 'edge-spaces-in-name': return `${count} קבצים ${isReplaceMode(mode) ? 'יוחלפו' : 'ייווצרו'} בשם שמתחיל או נגמר ברווח, כי האתר מבקש בדיוק את השם הזה. הרווחים נחתכים רק מהטקסט שמוקרא.`;
        case 'missing-not-replaced': return `${count} קבצים עוד לא קיימים, ולכן לא ייגעו בהם: מצב ההחלפה מחליף רק קבצים שקיימים. כדי ליצור אותם משתמשים במצב "${MODE_LABELS[MODE_FILL_MISSING]}".`;
        case 'browser-cache': return 'דפדפנים ששמרו כבר את הקובץ ימשיכו להשמיע את הישן עד שהמטמון שלהם יפוג (קובץ שהוחלף נשמר ליום אחד, קבצים ישנים עד שנה). בחלון פרטי שומעים את החדש. ב-S3 אין גרסאות, ולכן הגיבוי הוא הדרך היחידה לחזור אחורה.';
        case 'name-truncated': return `${count} שמות קבצים נחתכו ל-60 תווים, כמו שהאתר עושה.`;
        case 'same-name-different-text': return `${count} טקסטים מקבלים שם קובץ זהה לטקסט אחר אבל נקראים אחרת. יוקרא הראשון.`;
        case 'group-long-texts': {
            const maxChars = Number(String(warning?.message).match(/longer than (\d+) characters/)?.[1]) || FALLBACK_GROUP_LIMITS.maxItemChars;
            return `${count} טקסטים ארוכים מ-${formatNumber(maxChars)} תווים, ולכן הם לא חלק מהרצת קבוצה (בטקסט ארוך יש הפסקות פנימיות, והחיתוך לא היה מבדיל אותן מההפסקות בין המילים). את הקבצים האלה מכינים בהרצה בלי קבוצה.`;
        }
        case 'trial-folder': return 'במצב ניסוי הקבצים נכתבים לתיקייה משלהם ולא לקבצי האודיו של האתר, ולכן אף אחד לא שומע אותם באתר. מאזינים להם כאן, במסך ההרצה, ואם הכול תקין מריצים אותם באמת.';
        case 'google-daily-quota': return 'למפתח Google ברמה 1 יש מכסה של כ-100 בקשות ביום לכל מודל. ההרצה תושהה במכסה היומית (או תעבור לגיבוי, אם הוגדר), ואפשר להמשיך אותה למחרת.';
        case 'fallback-not-ready': return `אי אפשר להשתמש בגיבוי כרגע. ${String(warning.message).replace(/^The fallback cannot be used now:\s*/, '')}`;
        default: return String(warning?.message ?? '');
    }
}

/** A blocker of the plan (the run cannot start while there is one) in Hebrew */
export function describeBlocker(blocker, plan, options) {
    const message = String(blocker?.message ?? '');
    switch (blocker?.code) {
        case 'not-ready': return `השרת לא מוכן להרצה: ${message}`;
        case 'cost-over-cap': return `העלות המשוערת (${formatUsd(plan?.estimatedCostUsd)}) גבוהה מהתקרה (${formatUsd(plan?.maxCostUsd)}). מעלים את התקרה (עד ${limitsOf(options).maxCostUsd} דולר) או מקטינים את ההרצה.`;
        case 'existence-unchecked': return `אי אפשר היה לבדוק ב-S3 אם ${formatNumber(leadingNumber(message))} קבצים קיימים, ולכן ההרצה לא תתחיל. אפשר לנסות שוב. (${message})`;
        case 'too-many-files': {
            const advice = 'מקטינים את מספר הערכים, ומשתמשים ב"המשך אחרי מזהה" להרצה הבאה.';
            if (isTrialMode(modeOf(plan))) return `הניסוי כולל ${formatNumber(plannedFiles(plan))} קבצים, והוא מכין לכל היותר ${formatNumber(limitsOf(options).maxTrialFiles)}. ${advice}`;
            if (isReplaceMode(modeOf(plan))) return `ההחלפה כוללת ${formatNumber(plannedFiles(plan))} קבצים קיימים, וההרצה מחליפה לכל היותר ${formatNumber(limitsOf(options).maxReplaceFiles)}. ${advice}`;
            return `חסרים ${formatNumber(plannedFiles(plan))} קבצים, וההרצה יוצרת לכל היותר ${formatNumber(limitsOf(options).maxFiles)}. ${advice}`;
        }
        case 'nothing-to-create': return 'כל הקבצים כבר קיימים, אין מה ליצור.';
        case 'nothing-to-replace': return `אף אחד מהקבצים לא קיים, ולכן אין מה להחליף. כדי ליצור קבצים משתמשים במצב "${MODE_LABELS[MODE_FILL_MISSING]}".`;
        case 'nothing-to-try': return 'לא נשאר אף טקסט שאפשר לשלוח בקבוצה, ולכן אין מה לנסות.';
        case 'nothing-for-groups': return 'כל הטקסטים ארוכים מדי להרצת קבוצה. את הקבצים האלה מכינים בהרצה בלי קבוצה.';
        default: return message;
    }
}

// ____________________________________________________________________________________________
// what the server answers: the runs

export const JOB_STATUS_LABELS = { running: 'רצה', paused: 'מושהית', stopped: 'נעצרה', completed: 'הסתיימה' };

export const PAUSE_REASON_TEXT = {
    quota: 'המכסה היומית של הספק נגמרה. אפשר להמשיך מחר, או להגדיר ספק גיבוי.',
    auth: 'הספק לא קיבל את המפתח. מעדכנים את המפתח בשרת, ואז ממשיכים.',
    credit: 'נגמר האשראי אצל הספק. מטעינים אשראי, ואז ממשיכים.',
    'cost-cap': 'ההרצה הגיעה לתקרת העלות. אפשר להמשיך עם תקרה גבוהה יותר.',
    'too-many-failures': 'יותר מדי קבצים נכשלו אחד אחרי השני. בודקים את השגיאות של הקבצים, ואז ממשיכים.',
    error: 'ההרצה הושהתה בגלל שגיאה. קוראים את ההודעה, ואז ממשיכים.',
    'server-restart': 'השרת הופעל מחדש, וההרצה הושהתה. היא ממשיכה מעצמה, ואם זה לא קרה אפשר להמשיך אותה כאן.',
    backup: 'אי אפשר היה לגבות קובץ ישן, ולכן שום קובץ לא הוחלף ושום דבר לא שולם. קוראים את ההודעה (בדרך כלל חסרה הרשאה ל-S3, או ש-S3 לא זמין), פותרים, ואז ממשיכים.',
};

export const ITEM_STATUS_LABELS = { pending: 'ממתין', working: 'בעבודה', done: 'נוצר', skipped: 'כבר היה קיים', failed: 'נכשל' };
/** The same states in a run that replaces files: a file that does not exist is not created, it is left alone */
export const REPLACE_ITEM_STATUS_LABELS = { pending: 'ממתין', working: 'בעבודה', done: 'הוחלף', skipped: 'אין קובץ להחלפה', failed: 'נכשל' };
/** The same states in a trial: the file is made in the folder of the trial, and nothing on the site is touched */
export const TRIAL_ITEM_STATUS_LABELS = { pending: 'ממתין', working: 'בעבודה', done: 'נוצר לניסוי', skipped: 'דולג', failed: 'נכשל' };
export const ITEM_STATUSES = ['pending', 'working', 'done', 'skipped', 'failed'];
export const itemStatusLabels = (mode) => {
    if (isTrialMode(mode)) return TRIAL_ITEM_STATUS_LABELS;
    return isReplaceMode(mode) ? REPLACE_ITEM_STATUS_LABELS : ITEM_STATUS_LABELS;
};

/** How a file of a replacement run, or of a trial, ended (item.outcome) - more exact than its state */
export const ITEM_OUTCOME_LABELS = { replaced: 'הוחלף', 'already-replaced': 'הוחלף בניסיון קודם של ההרצה', 'file-missing': 'אין קובץ, לא נוצר', trial: 'נוצר לניסוי' };

/** What the two counters of a run count */
export const counterLabels = (mode) => {
    if (isTrialMode(mode)) return { done: 'נוצרו לניסוי', skipped: 'דולגו' };
    return isReplaceMode(mode) ? { done: 'הוחלפו', skipped: 'אין קובץ להחלפה' } : { done: 'נוצרו', skipped: 'כבר היו' };
};

/** The words on the chip of a file of a run: how a replacement or a trial ended, when the server says; otherwise its state */
export function describeItem(item, mode) {
    if ((isReplaceMode(mode) || isTrialMode(mode)) && item?.outcome && ITEM_OUTCOME_LABELS[item.outcome]) return ITEM_OUTCOME_LABELS[item.outcome];
    return itemStatusLabels(mode)[item?.status] ?? String(item?.status ?? '');
}

/** The folder with the old files of a replacement run (null for a run that only creates files) */
export const backupFolderOf = (job, options) => (isReplaceMode(modeOf(job)) ? (job?.backupFolder ?? `${backupPrefixOf(options)}${job?.id ?? ''}/`) : null);

/** The folder with the files of a trial (null for a run that makes live files) */
export const trialFolderOf = (job, options) => (isTrialMode(modeOf(job)) ? (job?.trialFolder ?? `${trialPrefixOf(options)}${job?.id ?? ''}/`) : null);

export const ERROR_KIND_LABELS = {
    'rate-limit': 'מגבלת קצב',
    quota: 'מכסה יומית',
    auth: 'מפתח לא תקין',
    credit: 'אין אשראי',
    'bad-request': 'בקשה שנדחתה',
    transient: 'תקלה זמנית',
    backup: 'הגיבוי נכשל',
    'backup-conflict': 'מקום הגיבוי תפוס או שהקובץ השתנה',
    group: 'לא הצליח בקבוצה',
    unknown: 'לא ידוע',
};

/** { label, severity } of a run - severity is the one of an MUI Chip / Alert: info, warning, success, error, default */
export function describeJob(job) {
    if (!job) return { label: '', severity: 'default' };
    if (job.stalled) return { label: 'נתקעה (ייתכן שהשרת הופעל מחדש)', severity: 'warning' };
    if (job.status === 'running') return { label: job.stopRequested ? 'נעצרת...' : JOB_STATUS_LABELS.running, severity: 'info' };
    if (job.status === 'paused') return { label: JOB_STATUS_LABELS.paused, severity: 'warning' };
    if (job.status === 'stopped') return { label: JOB_STATUS_LABELS.stopped, severity: 'default' };
    if (job.status === 'completed') {
        return (job.counters?.failed ?? 0) > 0
            ? { label: 'הסתיימה עם כשלונות', severity: 'error' }
            : { label: JOB_STATUS_LABELS.completed, severity: 'success' };
    }
    return { label: String(job.status ?? ''), severity: 'default' };
}

/** How far a run is: the files that are settled (created, found to exist already, or failed) out of all of them */
export function progressOf(job) {
    const counters = job?.counters ?? {};
    const total = counters.total ?? 0;
    const finished = (counters.done ?? 0) + (counters.skipped ?? 0) + (counters.failed ?? 0);
    return { total, finished, percent: total > 0 ? Math.min(100, Math.round((finished / total) * 100)) : 0 };
}

/** A run that works now: another one cannot start. */
export const isActiveJob = (job) => job?.status === 'running' && !job?.stalled;

/** "6 קבצים, 48 תווים" - what one provider made in a run */
export function describeUsage(usage) {
    return Object.entries(usage ?? {})
        .map(([provider, value]) => `${PROVIDER_LABELS[provider] ?? provider}: ${formatNumber(value?.files)} קבצים, ${formatNumber(value?.chars)} תווים`)
        .join(' | ');
}

// ____________________________________________________________________________________________
// the group mode: the numbers of a run, and the recordings of its groups (SCRUM-1882)

/** "2.3 MB", "540 KB" */
export function formatBytes(bytes) {
    const value = Number(bytes);
    if (!Number.isFinite(value) || value <= 0) return '0 KB';
    if (value < 1024 * 1024) return `${Math.max(1, Math.round(value / 1024)).toLocaleString('he-IL')} KB`;
    return `${(value / (1024 * 1024)).toLocaleString('he-IL', { maximumFractionDigits: 1 })} MB`;
}

/**
 * What a group run did, as lines for the page: [{ key, label, value, severity }]. A run that is not a group run has none (stats is null).
 * `recordingsFailed` shows only when it is not zero: a recording that was not kept costs the admin the listening to that group (the files are fine).
 */
export function describeGroupStats(stats) {
    if (!stats || typeof stats !== 'object') return [];
    const rows = [
        { key: 'requests', label: 'בקשות לקבוצות', value: formatNumber(stats.requests), severity: 'default' },
        { key: 'files', label: 'קבצים שנחתכו ונשמרו', value: formatNumber(stats.files), severity: 'default' },
        { key: 'audioSeconds', label: 'אורך ההקלטות', value: formatClock((Number(stats.audioSeconds) || 0) * 1000), severity: 'default' },
        { key: 'recordings', label: 'הקלטות שנשמרו להאזנה', value: formatNumber(stats.recordings), severity: 'default' },
    ];
    if ((stats.splits ?? 0) > 0) rows.push({ key: 'splits', label: 'קבוצות שפוצלו לשתיים (החיתוך לא התאים)', value: formatNumber(stats.splits), severity: 'warning' });
    if ((stats.singles ?? 0) > 0) rows.push({ key: 'singles', label: 'מילים שהוקראו לבד', value: formatNumber(stats.singles), severity: 'warning' });
    if ((stats.wastedUsd ?? 0) > 0) rows.push({ key: 'wastedUsd', label: 'עלות של הקלטות שלא נוצלו', value: formatUsd(stats.wastedUsd), severity: 'warning' });
    if ((stats.recordingsFailed ?? 0) > 0) rows.push({ key: 'recordingsFailed', label: 'הקלטות שלא נשמרו להאזנה (הקבצים עצמם תקינים)', value: formatNumber(stats.recordingsFailed), severity: 'warning' });
    return rows;
}

/** What became of a word of a group - as the server keeps it in the list of the recording (item.status) */
export const GROUP_ITEM_STATUS_LABELS = {
    success: 'נשמר באתר',
    replaced: 'הוחלף באתר',
    trial: 'נשמר לניסוי',
    failed: 'נכשל',
    rejected: 'נפסל, נוצר מחדש לבד',
    none: 'לא נשמר',
};
export const groupItemStatusLabel = (status) => GROUP_ITEM_STATUS_LABELS[status] ?? String(status ?? '');
/** The colour of the chip of a word of a group (a severity of an MUI Chip) */
export const groupItemSeverity = (status) => ({ success: 'success', replaced: 'success', trial: 'info', failed: 'error', rejected: 'warning' }[status] ?? 'default');

/** "קבוצה 3 · עברית · נקבה" - the name of a recording in the list */
export function groupTitle(group) {
    const parts = [`קבוצה ${formatNumber(group?.number)}`];
    if (group?.language) parts.push(languageLabel(group.language));
    if (group?.gender) parts.push(GENDER_LABELS[group.gender] ?? group.gender);
    return parts.join(' · ');
}

/** The facts of a recording, in the order that they are shown: voice, words, length, size */
export function groupFacts(group) {
    const facts = [];
    if (group?.voice) facts.push(group.voice);
    const words = Number(group?.items ?? 0);
    const stored = Number(group?.stored ?? 0);
    facts.push(stored === words ? wordsCount(words) : `${wordsCount(words)}, ${formatNumber(stored)} נשמרו`);
    facts.push(`אורך ${formatClock((Number(group?.seconds) || 0) * 1000)}`);
    facts.push(formatBytes(group?.bytes));
    return facts;
}

/** The player starts a little before a word, so that the first sound is not cut (the times of the server are the times of the sound) */
export const LEAD_IN_MS = 250;
export const seekSeconds = (startMs) => Math.max(0, ((Number(startMs) || 0) - LEAD_IN_MS) / 1000);

/**
 * Which word of the recording is heard at this second: the last word that has begun (its lead-in included) - the pause after a word is the
 * word's own. -1 before the first word (or when there are no words). The words are in the order of the recording.
 * The time is compared in whole milliseconds (the precision of the times of the server): seconds * 1000 can be a hair under the whole number
 * (16.15 * 1000 is 16149.999999999998), and a player that was sent to the start of a word must find that word and not the one before it.
 */
export function activeItemIndex(items, seconds) {
    const ms = Math.round(Number(seconds) * 1000);
    if (!Array.isArray(items) || items.length === 0 || !Number.isFinite(ms)) return -1;
    let low = 0;
    let high = items.length - 1;
    let found = -1;
    while (low <= high) {
        const middle = (low + high) >> 1;
        if ((Number(items[middle].startMs) || 0) - LEAD_IN_MS <= ms) {
            found = middle;
            low = middle + 1;
        } else {
            high = middle - 1;
        }
    }
    return found;
}

/** The words that were marked as wrong: a key is "<group number>:<n of the word in the group>" */
export const markKey = (groupNumber, n) => `${groupNumber}:${n}`;

/**
 * What is kept of a word that was marked: enough to name it in a message and to find it again (the run, the group and the place in it),
 * so the list of the marked words does not depend on which groups are open.
 */
export const markSnapshot = (group, item) => ({
    g: group.number,
    n: item.n,
    seq: item.seq ?? null,
    text: String(item.text ?? ''),
    fileName: String(item.fileName ?? ''),
    startMs: Number(item.startMs) || 0,
    language: group.language ?? null,
    gender: group.gender ?? null,
    voice: group.voice ?? null,
});

const isMarkSnapshot = (value) => Boolean(value) && typeof value === 'object'
    && Number.isInteger(value.g) && value.g > 0 && Number.isInteger(value.n) && value.n > 0
    && typeof value.text === 'string' && typeof value.fileName === 'string';

/**
 * The marks as the browser kept them (text), or {} when there is nothing, or it cannot be read. Only well-formed marks are taken, each one under
 * its own key - what the browser holds is never trusted to be what was written.
 */
export function parseMarks(raw) {
    if (typeof raw !== 'string' || raw === '') return {};
    let parsed;
    try {
        parsed = JSON.parse(raw);
    } catch {
        return {};
    }
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const marks = {};
    for (const value of Object.values(parsed)) {
        if (isMarkSnapshot(value)) {
            marks[markKey(value.g, value.n)] = markSnapshot({ number: value.g, language: value.language, gender: value.gender, voice: value.voice }, value);
        }
    }
    return marks;
}

/** The marked words in the order of the recordings (the group, then the place in it) */
export const sortedMarks = (marks) => Object.values(marks ?? {}).filter(isMarkSnapshot).sort((a, b) => a.g - b.g || a.n - b.n);

/** A table cell for a text that is pasted somewhere: no tab and no line break inside it */
const cell = (value) => String(value ?? '').replace(/[\t\r\n]+/g, ' ').trim();

/**
 * The marked words as a table for the chat (tab-separated, with a heading line), to hand back what sounds wrong: the run, the word, its file,
 * the group and the place in it.
 * @param {string} jobId
 * @param {Record<string, object>} marks the marks (markSnapshot)
 * @returns {string} '' when nothing is marked
 */
export function markedWordsText(jobId, marks) {
    const rows = sortedMarks(marks);
    if (rows.length === 0) return '';
    const lines = rows.map((row) => [
        row.seq ?? '',
        cell(row.text),
        cell(row.fileName),
        row.language ? languageLabel(row.language) : '',
        GENDER_LABELS[row.gender] ?? row.gender ?? '',
        cell(row.voice),
        `קבוצה ${row.g}`,
        formatClock(row.startMs),
    ].join('\t'));
    return [`הרצה ${jobId}`, ['מספר', 'מילה', 'שם הקובץ', 'שפה', 'מגדר', 'קול', 'קבוצה', 'מקום בהקלטה'].join('\t'), ...lines].join('\n');
}
